import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { IBaseSearchRequest, IPagedResults } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { environment } from 'src/environments/environment';
import { ICreateTimeEntryRequest, ISearchTimeEntriesRequest, IStartTimeEntryRequest, ITimeEntry, IUpdateTimeEntryRequest, SearchTimeEntryResponse } from '../models/time-entry';
import { ISignDayRequest, IConfirmDayRequest, IDayReportingObjectSearchRequest, IDayReportingObjectDetails, ISearchDayReportingObjectsResponse, IExportDayReportingObjectRequest } from '../models/day-reporting-object';
import { IWeekReportingObjectSearchRequest, ISignWeekRequest, IConfirmWeekRequest, IWeekReportingObject, ISearchWeekReportingObjectsResponse } from '../models/week-reporting-object';
import { IGetTimeEntryAnalyticsRequest, ITimeEntryAnalyticsResponse } from '../models/time-entry-analytics';
import { IEmployeeWorkTimeComparisonResponse, IGetEmployeeWorkTimeComparisonRequest } from '../models/employee-work-time-comparison';
import { IAssignmentTimeAnalyticsResponse, IGetAssignmentTimeAnalyticsRequest } from '../models/assignment-time-analytics';

@Injectable({
  providedIn: 'root'
})
export class TimeEntryService {

  constructor(private http: HttpClient) { }

  searchTimeEntries(request: ISearchTimeEntriesRequest){
    return this.http.post<SearchTimeEntryResponse>(
      `${environment.baseUrl}/v1/TimeEntry/searchTimeEntries`,
      request
    )
  }

  /**
   * The employees whose time data the current user may see: their own branch of the supervisor
   * hierarchy, or every employee with TimeEntries.Analytics. The reports and evaluations are
   * scoped the same way on the server, so this only keeps the dropdowns honest.
   */
  getVisibleEmployees(){
    return this.http.get<IEmployee[]>(
      `${environment.baseUrl}/v1/TimeEntry/getVisibleEmployees`
    )
  }

  /**
   * The employees (other than the current user) whose days and weeks the sign right reaches:
   * subordinates, or everyone with TimeEntries.SearchAll. Read-only reach (Analytics) does not
   * count. One's own rows are governed by the Self rights, which the client checks itself.
   */
  getManageableEmployees(){
    return this.http.get<IEmployee[]>(
      `${environment.baseUrl}/v1/TimeEntry/getManageableEmployees`
    )
  }

  /**
   * The employees (other than the current user) for whom time entries may be created, updated or
   * deleted: everyone with the All variant of the action, subordinates with the plain right,
   * nobody otherwise.
   */
  getWritableEmployees(action: 'Create' | 'Update' | 'Delete'){
    return this.http.get<IEmployee[]>(
      `${environment.baseUrl}/v1/TimeEntry/getWritableEmployees/${action}`
    )
  }

  getTimeEntryAnalytics(request: IGetTimeEntryAnalyticsRequest){
    return this.http.post<ITimeEntryAnalyticsResponse>(
      `${environment.baseUrl}/v1/TimeEntry/getTimeEntryAnalytics`,
      request
    )
  }

  getAssignmentTimeAnalytics(request: IGetAssignmentTimeAnalyticsRequest){
    return this.http.post<IAssignmentTimeAnalyticsResponse>(
      `${environment.baseUrl}/v1/TimeEntry/getAssignmentTimeAnalytics`,
      request
    )
  }

  getEmployeeWorkTimeComparison(request: IGetEmployeeWorkTimeComparisonRequest){
    return this.http.post<IEmployeeWorkTimeComparisonResponse>(
      `${environment.baseUrl}/v1/TimeEntry/getEmployeeWorkTimeComparison`,
      request
    )
  }

  getTimeEntry(id: string){
    return this.http.get<ITimeEntry>(
      `${environment.baseUrl}/v1/TimeEntry/getTimeEntry/${id}`
    )
  }

  createTimeEntry(request: ICreateTimeEntryRequest){
    return this.http.post<string>(
      `${environment.baseUrl}/v1/TimeEntry/createTimeEntry`,
      request
    )
  }

  updateTimeEntry(request: IUpdateTimeEntryRequest){
    return this.http.put<string>(
      `${environment.baseUrl}/v1/TimeEntry/updateTimeEntry/${request.id}`,
      request
    )
  }

  deleteTimeEntry(id: string){
    return this.http.delete<string>(
      `${environment.baseUrl}/v1/TimeEntry/deleteTimeEntry/${id}`
    )
  }

  startTimeEntry(request: IStartTimeEntryRequest){
    console.log("start Time")
    return this.http.post<ITimeEntry>(
      `${environment.baseUrl}/v1/TimeEntry/startTimeEntry`,
      request
    )
  }

  // startTimeEntryWithCategory(timeEntryCategoryId: string | null){
  //   console.log("start Time with category")
  //   const body = timeEntryCategoryId ? JSON.stringify(timeEntryCategoryId) : {};
  //   return this.http.post<ITimeEntry>(
  //     `${environment.baseUrl}/v1/TimeEntry/startTimeEntry`,
  //     body,
  //     {headers:{
  //       'Content-Type': 'application/json'
  //     }}
  //   )
  // }

  stopTimeEntry(){
    return this.http.post<ITimeEntry>(
      `${environment.baseUrl}/v1/TimeEntry/stopTimeEntry`,
      {}
    )
  }

  signDay(request: ISignDayRequest){
    return this.http.post<string>(
      `${environment.baseUrl}/v1/TimeEntry/signDay`,
      request
    )
  }

  confirmDay(request: IConfirmDayRequest){
    return this.http.post<string>(
      `${environment.baseUrl}/v1/TimeEntry/confirmDay`,
      request
    )
  }

  searchDayReports(request: IDayReportingObjectSearchRequest){
    return this.http.post<ISearchDayReportingObjectsResponse>(
      `${environment.baseUrl}/v1/TimeEntry/searchDayReports`,
      request
    )
  }

  searchWeekReports(request: IWeekReportingObjectSearchRequest){
    return this.http.post<ISearchWeekReportingObjectsResponse>(
      `${environment.baseUrl}/v1/TimeEntry/searchWeekReports`,
      request
    )
  }

  signWeek(request: ISignWeekRequest){
    return this.http.post<string>(
      `${environment.baseUrl}/v1/TimeEntry/signWeek`,
      request
    )
  }

  confirmWeek(request: IConfirmWeekRequest){
    return this.http.post<string>(
      `${environment.baseUrl}/v1/TimeEntry/confirmWeek`,
      request
    )
  }
  
  exportDevices(request: IExportDayReportingObjectRequest){
    return this.http.post<Blob>(
      `${environment.baseUrl}/v1/TimeEntry/exportDayReportingObjects`,
      request,
      { 
        responseType: 'blob' as 'json',
        observe: 'response'
      } 
    )
  }
}
